import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonSelect from "../../common/CommonSelect";
import { validateAddressField } from "./scriptcobillingaddresshelper";

const stateMapping = {
  AK: "Alaska",
  AL: "Alabama",
  AR: "Arkansas",
  AS: "American Samoa",
  AZ: "Arizona",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DC: "District of Columbia",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  GU: "Guam",
  HI: "Hawaii",
  IA: "Iowa",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  MA: "Massachusetts",
  MD: "Maryland",
  ME: "Maine",
  MI: "Michigan",
  MN: "Minnesota",
  MO: "Missouri",
  MP: "Northern Mariana Islands",
  MS: "Mississippi",
  MT: "Montana",
  NC: "North Carolina",
  ND: "North Dakota",
  NE: "Nebraska",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NV: "Nevada",
  NY: "New York",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  PR: "Puerto Rico",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VA: "Virginia",
  VI: "Virgin Islands",
  VT: "Vermont",
  WA: "Washington",
  WI: "Wisconsin",
  WV: "West Virginia",
  WY: "Wyoming",
};

const fullStates = Object.values(stateMapping).sort((a, b) =>
  a.localeCompare(b),
);

const ScriptCoPatientAddressAndNotes = ({
  address = {},
  patientNotes = "",
  isBillingAddress = false,
  onAddressChange,
  onNotesChange,
  onIsBillingAddressChange,
  onValidationChange,
  showErrors,
}) => {
  const anchorRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    const fields = ["street", "city", "state", "zip"];
    const newErrors = {};
    fields.forEach((field) => {
      const error = validateAddressField(field, address[field] || "");
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
  }, [address]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getErrorProps = (field) => {
    const isInvalid = !!errors[field];
    const shouldShow = touched[field] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[field] : "",
    };
  };

  const handleOpenSelect = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSelect = () => {
    setAnchorEl(null);
  };

  const displayState =
    stateMapping[address.state] || address.state || "Select State";

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2.5,
        border: "1px solid #E8E8E9",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
        >
          Patient Address & Notes
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!isBillingAddress}
              onChange={(e) => onIsBillingAddressChange?.(e.target.checked)}
              size="small"
              sx={{
                color: "#403C6F !important",
                "&.Mui-checked": {
                  color: "#403C6F !important",
                },
                "& .MuiSvgIcon-root": {
                  color: "#403C6F !important",
                },
              }}
            />
          }
          label={
            <Typography
              sx={{ fontSize: "13px", fontWeight: 500, color: "#403C6F" }}
            >
              Is Billing Address
            </Typography>
          }
          sx={{ m: 0 }}
        />
      </Box>

      {/* Row 1: Street Address & Apt/Suite/Line */}
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Address Line 1<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            autoComplete="new-password"
            placeholder="Enter address line 1"
            value={address.street || ""}
            onChange={(e) => {
              if (e.target.value.length <= 60)
                onAddressChange("street", e.target.value);
            }}
            onBlur={() => handleBlur("street")}
            {...getErrorProps("street")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{ fontSize: "12px", color: "#666", mr: 0.5 }}
                    >
                      {(address.street || "").length}/60
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("street").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Address Line 2
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            autoComplete="new-password"
            placeholder="Enter address line 2"
            value={address.line === "-" ? "" : address.line || ""}
            onChange={(e) => {
              if (e.target.value.length <= 60)
                onAddressChange("line", e.target.value);
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{ fontSize: "12px", color: "#666", mr: 0.5 }}
                    >
                      {(address.line === "-" ? "" : address.line || "").length}
                      /60
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#E8E8E9" },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>
      </Box>

      {/* Row 2: City, State, Zip */}
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            City<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            autoComplete="new-password"
            placeholder="Enter city"
            value={address.city || ""}
            onChange={(e) => {
              if (e.target.value.length <= 50)
                onAddressChange("city", e.target.value);
            }}
            onBlur={() => handleBlur("city")}
            {...getErrorProps("city")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{ fontSize: "12px", color: "#666", mr: 0.5 }}
                    >
                      {(address.city || "").length}/50
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("city").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            State<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <Box
            ref={anchorRef}
            onClick={handleOpenSelect}
            sx={{
              width: "100%",
              height: "45px",
              border: "1px solid",
              borderColor: getErrorProps("state").error ? "#d32f2f" : "#E8E8E9",
              borderRadius: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 12px",
              backgroundColor: "#FFFFFF",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: address.state ? "#0e1324" : "#999893",
              }}
            >
              {displayState}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                color: getErrorProps("state").error ? "#d32f2f" : "#6B7280",
                fontSize: "20px",
                transition: "transform 0.2s",
                transform: anchorEl ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
          {getErrorProps("state").error && (
            <Typography
              sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0, ml: 1.75 }}
            >
              {getErrorProps("state").helperText}
            </Typography>
          )}
          <CommonSelect
            id="patient-state-select"
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={handleCloseSelect}
            noteTypes={fullStates}
            selectedTypes={[displayState]}
            handleTypeToggle={(type) => {
              const code = Object.keys(stateMapping).find(
                (key) => stateMapping[key] === type,
              );
              onAddressChange("state", code || type);
              handleCloseSelect();
            }}
            width={
              anchorRef.current?.offsetWidth
                ? `${anchorRef.current.offsetWidth}px`
                : "240px"
            }
          />
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Zip<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            autoComplete="new-password"
            placeholder="Enter zip code"
            value={address.zip || ""}
            onChange={(e) => {
              if (e.target.value.length <= 10)
                onAddressChange("zip", e.target.value);
            }}
            onBlur={() => handleBlur("zip")}
            {...getErrorProps("zip")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{ fontSize: "12px", color: "#666", mr: 0.5 }}
                    >
                      {(address.zip || "").length}/10
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("zip").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>
      </Box>

      {/* Row 4: Patient Notes */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
        >
          Patient Notes
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          multiline
          rows={2}
          value={patientNotes || ""}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Enter patient notes, clinical considerations, or billing comments..."
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": { borderColor: "#E8E8E9" },
            },
            "& .MuiInputBase-input": { fontSize: "14px", color: "#0e1324" },
          }}
        />
      </Box>
    </Box>
  );
};

ScriptCoPatientAddressAndNotes.propTypes = {
  address: PropTypes.object,
  patientNotes: PropTypes.string,
  isBillingAddress: PropTypes.bool,
  onAddressChange: PropTypes.func.isRequired,
  onNotesChange: PropTypes.func.isRequired,
  onIsBillingAddressChange: PropTypes.func,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default ScriptCoPatientAddressAndNotes;
